import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react';
import type { ThemeConfig } from '@devfactory/core';

// =============================================================================
// THEME CONTEXT
// =============================================================================

interface ThemeContextValue {
  theme: ThemeConfig;
  cssVariables: Record<string, string>;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
}

// =============================================================================
// COLOR UTILITIES
// =============================================================================

function hexToHSL(hex: string): { h: number; s: number; l: number } {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return { h: 0, s: 0, l: 0 };

  let r = parseInt(result[1] ?? '0', 16) / 255;
  let g = parseInt(result[2] ?? '0', 16) / 255;
  let b = parseInt(result[3] ?? '0', 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  return { h: Math.round(h * 360), s: Math.round(s * 100), l: Math.round(l * 100) };
}

function generateColorScale(hex: string): Record<string, string> {
  const { h, s } = hexToHSL(hex);
  return {
    '50': `hsl(${h}, ${s}%, 95%)`,
    '100': `hsl(${h}, ${s}%, 90%)`,
    '200': `hsl(${h}, ${s}%, 80%)`,
    '300': `hsl(${h}, ${s}%, 70%)`,
    '400': `hsl(${h}, ${s}%, 60%)`,
    '500': `hsl(${h}, ${s}%, 50%)`,
    '600': `hsl(${h}, ${s}%, 40%)`,
    '700': `hsl(${h}, ${s}%, 30%)`,
    '800': `hsl(${h}, ${s}%, 20%)`,
    '900': `hsl(${h}, ${s}%, 10%)`,
  };
}

// =============================================================================
// THEME PROVIDER
// =============================================================================

interface ThemeProviderProps {
  theme: ThemeConfig;
  children: ReactNode;
}

export function ThemeProvider({ theme, children }: ThemeProviderProps) {
  const cssVariables = useMemo(() => {
    const primaryScale = generateColorScale(theme.primaryColor);
    const borderRadiusMap: Record<string, string> = {
      none: '0',
      sm: '0.25rem',
      md: '0.375rem',
      lg: '0.5rem',
    };

    return {
      '--color-primary-50': primaryScale['50'] ?? '',
      '--color-primary-100': primaryScale['100'] ?? '',
      '--color-primary-200': primaryScale['200'] ?? '',
      '--color-primary-300': primaryScale['300'] ?? '',
      '--color-primary-400': primaryScale['400'] ?? '',
      '--color-primary-500': primaryScale['500'] ?? '',
      '--color-primary-600': primaryScale['600'] ?? '',
      '--color-primary-700': primaryScale['700'] ?? '',
      '--color-primary-800': primaryScale['800'] ?? '',
      '--color-primary-900': primaryScale['900'] ?? '',
      '--color-primary': theme.primaryColor,
      '--border-radius': borderRadiusMap[theme.borderRadius] ?? '0.375rem',
      '--font-family': theme.fontFamily ?? 'Inter, system-ui, sans-serif',
    };
  }, [theme]);

  useEffect(() => {
    // Apply CSS variables to document
    const root = document.documentElement;
    Object.entries(cssVariables).forEach(([key, value]) => {
      root.style.setProperty(key, value ?? '');
    });

    // Apply dark mode
    if (theme.mode === 'dark') {
      root.classList.add('dark');
    } else if (theme.mode === 'light') {
      root.classList.remove('dark');
    } else {
      // System preference
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      root.classList.toggle('dark', prefersDark);
    }

    return () => {
      Object.keys(cssVariables).forEach((key) => {
        root.style.removeProperty(key);
      });
    };
  }, [cssVariables, theme.mode]);

  return (
    <ThemeContext.Provider value={{ theme, cssVariables }}>
      {children}
    </ThemeContext.Provider>
  );
}
